export type FootballShotOutcome = "goal" | "save" | "wide";
export const FOOTBALL_RESET_EVENT = "tnb:football-reset";
type Point = { x: number; y: number };
const clamp = (value: number, low: number, high: number) => Math.max(low, Math.min(high, value));

/** Decorative mini-game geometry, independent of predictions, odds and betting data. */
export function createFootballShot(
  origin: { x: number; y: number; width: number },
  viewport: { width: number; height: number },
  random: () => number = Math.random,
) {
  const size = origin.width;
  const margin = 16;
  const goalWidth = 120;
  const left = random() < 0.5;
  const variation = random() * Math.min(100, viewport.width * 0.18);
  const goalX = left ? margin + variation : viewport.width - goalWidth - margin - variation;
  const goalY = clamp(105 + random() * 90, 16, Math.max(16, viewport.height - 100));
  const outcome = (["goal", "save", "wide"] as const)[Math.floor(random() * 3)]!;
  const start = { x: origin.x + size / 2, y: origin.y + size / 2 };
  const end = {
    x:
      outcome === "wide"
        ? left
          ? goalX + goalWidth + 32
          : goalX - 32
        : goalX + 22 + random() * (goalWidth - 44),
    y: goalY + 18 + random() * 25,
  };
  end.x = clamp(end.x, 24, viewport.width - 24);
  const control = {
    x: clamp(
      (start.x + end.x) / 2 + (random() - 0.5) * viewport.width * 0.45,
      24,
      viewport.width - 24,
    ),
    y: Math.max(8, Math.min(start.y, end.y) - 60 - random() * 120),
  };
  function point(t: number): Point {
    return {
      x: (1 - t) ** 2 * start.x + 2 * (1 - t) * t * control.x + t ** 2 * end.x - size / 2,
      y: (1 - t) ** 2 * start.y + 2 * (1 - t) * t * control.y + t ** 2 * end.y - size / 2,
    };
  }
  const result =
    outcome === "goal"
      ? {
          title: "GOAL!",
          detail: ["Top corner!", "Great finish!", "What a strike!"][Math.floor(random() * 3)]!,
        }
      : outcome === "save"
        ? { title: "SAVED!", detail: "The keeper gets there." }
        : { title: "OFF TARGET", detail: "Wide of the post." };
  return {
    outcome,
    result,
    goalX,
    goalY,
    size,
    start: point(0),
    quarter: point(0.25),
    middle: point(0.5),
    threeQuarter: point(0.75),
    end: point(1),
    duration: Math.round(720 + random() * 430),
    spin: Math.round((random() < 0.5 ? -1 : 1) * (480 + random() * 600)),
    rebound: outcome === "save" ? (left ? 28 : -28) : 0,
  };
}
